import type { ComponentChildren } from 'preact'
import { ListChecks } from 'lucide-preact'
import type { WebViewDto } from '@/api/webs'
import { formatQuizDate } from '@/utils/quizDate'
import { parseServerDate } from '@/utils/time'

/** Which version is live and what a save does to it. ApprovedAt and ApprovedByName survive a reopen. */
function versionLine(web: WebViewDto): string {
    const version = web.Download?.SignedOffQuizVersion
    if (version == null) return 'No version signed off yet. What you save here goes live at the next sign-off.'
    const approvedAt = parseServerDate(web.ApprovedAt)
    const date = approvedAt ? ` ${formatQuizDate(approvedAt)}` : ''
    const by = web.ApprovedByName ? ` by ${web.ApprovedByName}` : ''
    return web.ApprovalStatus === 'Signed Off'
        ? `Signed off${date}${by}. The live page, the results email and the report all use this version.`
        : `Signed off${date}${by}. The live page, the results email and the report keep using Version ${version} until the web is signed off again.`
}

/** The Quiz card's title row, shared by the read view and the editor; each passes its own actions. */
export function QuizHeading({ web, children }: { web: WebViewDto; children?: ComponentChildren }) {
    const version = web.Download?.SignedOffQuizVersion
    return (
        <div className="px-6 pt-6 pb-5 flex items-start justify-between gap-8">
            <div>
                <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 dark:text-white"><ListChecks className="w-5 h-5 text-primary-600" /> Quiz</h2>
                    {version != null && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">Version {version}</span>}
                </div>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{versionLine(web)}</p>
            </div>
            {children}
        </div>
    )
}
